import { useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";

import GlancePrism from "components/Screens/Marketplace/Customer/ProfileSection/GlancePrism";
import type { AiSummaryCapability, AiSummarySentiment } from "components/Screens/Marketplace/Customer/types";
import "components/Screens/Marketplace/Customer/ProfileSection/GlanceSection.scss";

interface GlanceSectionProps {
  firstName: string;
  /** aiSummary.recruiterPitch: what the person does. */
  pitch: string;
  /** aiSummary.personalitySnapshot: how they work; the reveal shows only when there is one. */
  snapshot?: string | null;
  /** Called when the snapshot is opened (not when it is closed). */
  onExpand?: () => void;
  /** This viewer's earlier vote on the pitch, if any. */
  vote?: AiSummarySentiment | null;
  /** Sends a vote on the texts shown; resolves false when it was not saved. Without it there is no feedback row. */
  onFeedback?: (sentiment: AiSummarySentiment, capabilities: AiSummaryCapability[]) => Promise<boolean>;
}

/**
 * Prototype glanceSection(): the AI pitch beside the animated prism, "See how {name} works" for the personality
 * snapshot, then the AI note with "Did you find this helpful?" on one footer line.
 */
function GlanceSection({ firstName, pitch, snapshot, onExpand, vote = null, onFeedback }: GlanceSectionProps) {
  const [open, setOpen] = useState(false);
  // The snapshot counts as shown once it has been opened, even if it was closed again.
  const [snapshotSeen, setSnapshotSeen] = useState(false);
  const [voted, setVoted] = useState<AiSummarySentiment | null>(vote);
  const [sending, setSending] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const Chevron = open ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;
  const toggle = () => {
    if (!open) {
      onExpand?.();
      setSnapshotSeen(true);
    }
    setOpen(!open);
  };
  const send = async (sentiment: AiSummarySentiment) => {
    if (!onFeedback || voted || sending) return;
    setSending(true);
    setVoted(sentiment);
    const shown: AiSummaryCapability[] = snapshot && snapshotSeen ? ["recruiter_pitch", "personality_snapshot"] : ["recruiter_pitch"];
    const saved = await onFeedback(sentiment, shown).catch(() => false);
    if (!saved) setVoted(null);
    setSending(false);
  };

  return (
    <section ref={cardRef} id="glance" className="profile-content__section glance">
      <div className="glance__title">{firstName} at a glance</div>
      <div className="glance__row">
        <div className="glance__body">
          <p className="glance__text">{pitch}</p>
          {snapshot && open && <p className="glance__text glance__text--how">{snapshot}</p>}
          {snapshot && (
            <button type="button" className="glance__pill" aria-expanded={open} onClick={toggle}>
              {open ? "Show less" : `See how ${firstName} works`}
              <Chevron aria-hidden />
            </button>
          )}
        </div>
        <GlancePrism cardRef={cardRef} />
      </div>
      <div className="glance__foot">
        <p className="glance__note">
          Written with AI from {firstName}&apos;s work history and Edge assessments. Use it as a starting point.
        </p>
        {onFeedback && (
          <div className="glance__feedback">
            {voted ? (
              <span className="glance__thanks" role="status">
                Thanks, that helps us write better ones.
              </span>
            ) : (
              <>
                <span className="glance__ask">Did you find this helpful?</span>
                {/* Thumbs instead of words (product, 2026-10-22); the names stay for screen readers and the tooltip. */}
                <button type="button" aria-label="This helps" title="This helps" disabled={sending} onClick={() => send("POSITIVE")}>
                  <ThumbUpOutlinedIcon aria-hidden />
                </button>
                <button type="button" aria-label="Not quite" title="Not quite" disabled={sending} onClick={() => send("NEGATIVE")}>
                  <ThumbDownOutlinedIcon aria-hidden />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default GlanceSection;
